import { CloseOutlined, EditFilled, UserOutlined } from "@ant-design/icons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/react-hooks";
import {
  Avatar,
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Input,
  message,
  Row,
  Select,
  Tooltip,
  Upload,
} from "antd";
import ImgCrop from "antd-img-crop";
import React, { useEffect, useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import {
  CONFIRM_USER_DETAILS,
  INSERT_EMPLOYEES,
  INSERT_USER_REPORTING,
} from "../../../Graphql/mutations";
import {
  GET_BUSINESS_DETAILS,
  GET_EMAIL_PHONE_DETAILS,
  GET_REPORTING_MANAGERS_BY_LOCATION_AND_ACCESS_TYPE,
  GET_USER_ID_BY_EMAIL,
} from "../../../Graphql/queries";
import { generateRandomPassword } from "../../../Utils/functions/common";
import { onFileUpload } from "../../Helper/MediaUploader";
import { Loader } from "../../Loader";
import { ShiftTiming } from "../../ShiftTiming/ShiftTiming";
import "./AddEmployeeDrawer.less";

const { REACT_APP_ASSETS_URL } = process.env;

export const AddEmployeeDrawer = ({ visible, toggleDrawer }) => {
  const [form] = Form.useForm();
  const [locationOptions, setLocationOptions] = useState([]);
  const [userRoleOptions, setUserRoleOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [currentWorkingHours, setCurrentWorkingHours] = useState(undefined);
  const [profileImage, setProfileImage] = useState("");

  const { data, loading } = useQuery(GET_BUSINESS_DETAILS, {
    variables: {
      user_id: localStorage.getItem("user_id"),
    },
  });

  const [
    GET_REPORTING_MANAGER_QUERY,
    { data: managerData, loading: isManagerDataLoading },
  ] = useLazyQuery(GET_REPORTING_MANAGERS_BY_LOCATION_AND_ACCESS_TYPE);

  const [REFETCH_GET_EMAIL_PHONE_DETAILS, { data: getEmailPhoneDetails }] =
    useLazyQuery(GET_EMAIL_PHONE_DETAILS, { fetchPolicy: "cache-and-network" });

  const [CONFIRM_USER_DETAILS_MUTATION] = useMutation(CONFIRM_USER_DETAILS);

  const [INSERT_EMPLOYEES_MUTATION, { loading: isInsertingEmployee }] =
    useMutation(INSERT_EMPLOYEES);

  const [
    INSERT_USER_REPORTING_MUTATION,
    { loading: isInsertingEmployeeReporting },
  ] = useMutation(INSERT_USER_REPORTING);

  useEffect(() => {
    if (data) {
      let roleOptionList =
        data.businessDetailsByUser[0].businessDetails.rolesByBusiness.roleList.flatMap(
          (singleRole) => {
            if (
              singleRole.job_role.accessDetails.access_level >
              data.businessDetailsByUser[0].userDetails.accessDetails
                .access_level
            ) {
              return [
                {
                  roleId: singleRole.job_role.role_id,
                  roleName: singleRole.job_role.role_name,
                  access_level: singleRole.job_role.accessDetails.access_level,
                },
              ];
            } else {
              return null;
            }
          }
        );
      let locationList =
        data.businessDetailsByUser[0].businessDetails.locations.flatMap(
          ({
            locationDetails: { location_id, location_name, workingHours },
          }) => {
            return [
              {
                location_id,
                location_name,
                workingHours: {
                  openingTime:
                    workingHours.length &&
                    workingHours.reduce(
                      (min, curr) =>
                        curr.opening_time < min ? curr.opening_time : min,
                      workingHours[0].opening_time
                    ),
                  closingTime:
                    workingHours.length &&
                    workingHours.reduce(
                      (max, curr) =>
                        curr.closing_time > max ? curr.closing_time : max,
                      workingHours[0].closing_time
                    ),
                },
              },
            ];
          }
        );
      roleOptionList = roleOptionList.filter((x) => !!x);
      setUserRoleOptions(roleOptionList);
      setLocationOptions(locationList);
    }
  }, [data]);

  const onLocationOrRoleUpdate = () => {
    let { location_id, role_id: employee_role_id } = form.getFieldsValue([
      "location_id",
      "role_id",
    ]);
    if (location_id && employee_role_id) {
      GET_REPORTING_MANAGER_QUERY({
        variables: {
          location_id,
          employee_access_id: userRoleOptions.find(
            (r) => r.roleId === employee_role_id
          ).access_level,
        },
      });
    }
  };
  useEffect(() => {
    if (getEmailPhoneDetails?.getUsers.length > 0) {
      getEmailPhoneDetails?.getUsers.forEach((e) => {
        if (e.email.includes(emailAddress)) {
          form.setFields([
            {
              name: "email",
              errors: ["Email Id already in-use"],
            },
          ]);
        } else if (e.phone.includes(phoneNumber)) {
          form.setFields([
            {
              name: "phone",
              errors: ["Phone number already in-use"],
            },
          ]);
        }
      });
    }
  }, [getEmailPhoneDetails, emailAddress, phoneNumber]);

  useEffect(() => {
    if (managerData) {
      setManagerOptions(
        managerData.users.flatMap((u) => [
          {
            first_name: u.userDetails.first_name,
            last_name: u.userDetails.last_name,
            user_id: u.userDetails.user_id,
          },
        ])
      );
    }
  }, [managerData]);
  const onInsertEmail = (email) => {
    REFETCH_GET_EMAIL_PHONE_DETAILS({
      variables: {
        phone: "",
        email: email ?? " ",
      },
    });
  };
  const onInsertPhone = (phone) => {
    REFETCH_GET_EMAIL_PHONE_DETAILS({
      variables: {
        phone: phone ?? "",
        email: "",
      },
    });
  };
  const onAddEmployee = async (values) => {
    setPhoneNumber(values.phone);
    setEmailAddress(values.email);
    REFETCH_GET_EMAIL_PHONE_DETAILS({
      variables: {
        phone: values.phone,
        email: values.email,
      },
    });

    try {
      const isInsertSuccessful = await INSERT_EMPLOYEES_MUTATION({
        variables: {
          objects: [
            {
              email: values.email,
              employee_code: values.employee_code,
              first_name: values.first_name,
              last_name: values.last_name,
              phone: values.phone,
              password: generateRandomPassword(),
              user_location_maps: [
                {
                  business_id:
                    data.businessDetailsByUser[0].businessDetails.business_id,
                  job_role_id: values.role_id,
                  location_id: values.location_id,
                },
              ],
              user_shifts: values.shift_timing,
            },
          ],
        },
      });
      if (!isInsertSuccessful.errors) {
        GET_USER_ID_BY_EMAIL_QUERY({
          variables: {
            email: values.email,
          },
        });
      } else {
        message.error(isInsertSuccessful.errors[0].message);
        setPhoneNumber();
        setEmailAddress();
      }
    } catch (error) {
      message.error();
    }
  };

  const insertReportingData = async (userData) => {
    try {
      const isInsertReportingSuccessful = await INSERT_USER_REPORTING_MUTATION({
        variables: {
          reportee_id: userData.users[0].user_id,
          reporter_id: form.getFieldValue("reporting_to"),
          location_id: form.getFieldValue("location_id"),
        },
      });
      if (!isInsertReportingSuccessful.errors) {
        form.resetFields();
        toggleDrawer();
        message.success("Employee data saved successfully!");
      }
    } catch (error) {}
  };

  const [GET_USER_ID_BY_EMAIL_QUERY] = useLazyQuery(GET_USER_ID_BY_EMAIL, {
    onCompleted: insertReportingData,
  });

  const onUploadFileSuccess = (fileUrl) => {
    setProfileImage(fileUrl);
    form.setFieldsValue({
      profileImage: fileUrl,
    });
  };

  return (
    <Drawer
      placement="right"
      closable={false}
      width={720}
      visible={visible}
      maskStyle={{ backgroundColor: "#000000CC" }}
    >
      <Row className="headerRow" justify="space-between" align="middle">
        <Col>
          <h5 className="mb-0">Add New Employee</h5>
        </Col>
        <Col>
          <Button type="text" icon={<CloseOutlined />} onClick={toggleDrawer} />
        </Col>
      </Row>
      {loading ? (
        <Loader />
      ) : (
        <Form form={form} onFinish={onAddEmployee}>
          <Row justify="center mt-5">
            <Form.Item name="profileImage" noStyle>
              <div style={{ position: "relative" }}>
                {!profileImage ? (
                  <Avatar
                    icon={<UserOutlined />}
                    size={100}
                    className="userAvatar"
                  >
                    {form.getFieldValue("first_name") +
                      form.getFieldValue("last_name")}
                  </Avatar>
                ) : (
                  <Avatar
                    src={REACT_APP_ASSETS_URL + profileImage}
                    icon={<UserOutlined />}
                    size={100}
                    className="userAvatar"
                  ></Avatar>
                )}
                <ImgCrop
                  zoom={false}
                  modalOk={"Crop"}
                  modalTitle={"Crop Image"}
                >
                  <Upload
                    showUploadList={false}
                    customRequest={(e) =>
                      onFileUpload(e, "profileImage", onUploadFileSuccess)
                    }
                  >
                    <Button
                      type="primary"
                      className="editButton"
                      icon={<EditFilled />}
                    ></Button>
                  </Upload>
                </ImgCrop>
              </div>
            </Form.Item>
          </Row>
          <Row className="mt-4 mx-3" gutter={16}>
            <Col xs={24} sm={12}>
              <Tooltip
                trigger={["focus"]}
                title="First Name"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="first_name"
                  rules={[
                    {
                      required: true,
                      message: "First Name is required",
                      whitespace: true,
                    },
                    {
                      pattern: new RegExp(
                        "^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$"
                      ),
                      message: "Invalid First name",
                    },
                  ]}
                >
                  <Input name="firstname" placeholder="First Name" />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12}>
              <Tooltip
                trigger={["focus"]}
                title="Last Name"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="last_name"
                  rules={[
                    {
                      required: true,
                      message: "Last Name is required",
                      whitespace: true,
                    },
                    {
                      pattern: new RegExp(
                        "^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$"
                      ),
                      message: "Invalid last name",
                    },
                  ]}
                >
                  <Input name="lastname" placeholder="Last Name" />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12}>
              <Tooltip
                trigger={["focus"]}
                title="Employee Code"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="employee_code"
                  rules={[
                    {
                      required: true,
                      message: "Employee Code is required",
                      whitespace: true,
                    },
                  ]}
                >
                  <Input name="code" placeholder="Employee Code" />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12}>
              <Tooltip
                trigger={["focus"]}
                title="User Role"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="role_id"
                  hasFeedback
                  rules={[
                    { required: true, message: "Please select a user role" },
                  ]}
                >
                  <Select
                    placeholder="User Role"
                    onChange={onLocationOrRoleUpdate}
                  >
                    {userRoleOptions.map((singleRole, index) => (
                      <Select.Option key={index} value={singleRole.roleId}>
                        {singleRole.roleName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12}>
              <Tooltip
                trigger={["focus"]}
                title="Email"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: "Email Address is required",
                      whitespace: true,
                    },
                    {
                      type: "email",
                      message: "Enter Valid Email Address",
                    },
                  ]}
                >
                  <Input
                    name="email"
                    placeholder="Email"
                    onBlur={(email) => {
                      setEmailAddress(email.target.value.replaceAll(" ", ""));
                      onInsertEmail(email.target.value.replaceAll(" ", ""));
                    }}
                  />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12}>
              <Tooltip
                trigger={["focus"]}
                title="Phone"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="phone"
                  rules={[
                    {
                      required: true,
                      message: "Phone Number is required",
                      whitespace: true,
                    },
                    () => ({
                      validator(_, value) {
                        if (value) {
                          if (isValidPhoneNumber(value)) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject(
                              new Error("Enter Valid Phone Number")
                            );
                          }
                        } else {
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                >
                  <PhoneInput
                    className="phoneInput_blk"
                    defaultCountry="US"
                    placeholder="(201) 555-1234"
                    onBlur={(phone) => {
                      setPhoneNumber(phone.target.value.replaceAll(" ", ""));
                      onInsertPhone(phone.target.value.replaceAll(" ", ""));
                    }}
                    countryOptionsOrder={["US", "CA", "|", "..."]}
                  />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12}>
              <Tooltip
                trigger={["focus"]}
                title="Location"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="location_id"
                  hasFeedback
                  rules={[
                    { required: true, message: "Please select a location" },
                  ]}
                >
                  <Select
                    placeholder="Location"
                    onChange={() => {
                      setCurrentWorkingHours(
                        locationOptions.find(
                          (l) =>
                            l.location_id === form.getFieldValue("location_id")
                        ).workingHours
                      );
                      onLocationOrRoleUpdate();
                    }}
                  >
                    {locationOptions.map((singleLocation, index) => (
                      <Select.Option
                        key={index}
                        value={singleLocation.location_id}
                      >
                        {singleLocation.location_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12}>
              <Tooltip
                trigger={["focus"]}
                title="Reporting Manager"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="reporting_to"
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Please select a reporting manager",
                    },
                  ]}
                >
                  <Select
                    disabled={
                      !form.getFieldValue("location_id") ||
                      !form.getFieldValue("role_id")
                    }
                    loading={isManagerDataLoading}
                    placeholder="Reporting Manager"
                  >
                    {managerOptions.map((singleManager, index) => (
                      <Select.Option key={index} value={singleManager.user_id}>
                        {singleManager.first_name +
                          " " +
                          singleManager.last_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Tooltip>
            </Col>
          </Row>
          <div className="px-4">
            <div className="emplyshiftTiming">
              <div className="secSubTitle pageSubTilte">
                <div className="stLeft">
                  <h5>Shift schedule</h5>
                </div>
                <div className="stright d-flex align-items-center"></div>
              </div>
              <Card>
                <ShiftTiming
                  locationHours={currentWorkingHours}
                  updateFormValue={form.setFieldsValue}
                  locationChange={form.getFieldValue("location_id")}
                />
              </Card>
            </div>
            <Form.Item
              name="shift_timing"
              rules={[
                {
                  required: true,
                  message: "Shift Timing is required",
                  whitespace: true,
                  type: "array",
                },
              ]}
            ></Form.Item>
          </div>
          <div className="mt-3 mb-4 text-center">
            <Button
              type="primary"
              shape="round"
              size="large"
              htmlType="submit"
              loading={isInsertingEmployee || isInsertingEmployeeReporting}
            >
              Submit
            </Button>
          </div>
        </Form>
      )}
    </Drawer>
  );
};
